import * as Sentry from '@sentry/vue';
import '@vue-flow/controls/dist/style.css';

import '@/assets/main.scss';

import { createApp } from 'vue';

import App from '@/App.vue';
import { useAuth } from '@/composable/msal';
import vuetify from '@/plugins/vuetify';
import router from '@/router';
import { RouteNames } from '@/router/constants/routeName';

const auth = useAuth();
const app = createApp(App).use(vuetify);
if (import.meta.env.VITE_STAGE !== 'local') {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_STAGE,
    release: import.meta.env.VITE_COMMIT,
    sampleRate: 0.1,
    // Sentry v11 collects more data by default; keep the v10 restrictive behavior.
    // See https://docs.sentry.io/platforms/javascript/migration/v10-to-v11/#data-collection
    dataCollection: {
      // Do not attach user identity or inferred IP information to events.
      userInfo: false,
      // Do not collect browser cookies.
      cookies: false,
      // Keep potentially identifying request and response headers scrubbed.
      httpHeaders: {
        // Remove forwarding, IP, remote, proxy, and user-related request headers.
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        // Apply the same scrubbing rules to response headers.
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      // Do not collect request or response bodies.
      httpBodies: [],
      // Keep sensitive values out of URL query parameters.
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
    },
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.1,
    integrations: [
      Sentry.replayIntegration(),
      Sentry.browserSessionIntegration(),
      Sentry.browserTracingIntegration(),
    ],
  });
}

auth
  .init()
  .then(() => {
    app.use(router);
    app.mount('#app');
  })
  .catch((error) => {
    app.use(router);
    app.mount('#app');
    Sentry.captureException(error);
    router.push({ name: RouteNames.Error });
  });
